import { getApiUrl } from '@/js/utils/getApiUrl';
import { getBitrixSessid } from '@/js/utils/getBitrixSessid';
import { isManagerResidentsMockEnabled } from './is-manager-residents-mock';
import {
  getMockManagerResidentDetail,
  getMockManagerResidentsList,
  mockManagerResidentStatusOptions,
  updateMockManagerResidentCluster,
  updateMockManagerResidentStatus,
} from './mocks';
import {
  MANAGER_RESIDENTS_PAGE_SIZE,
  type CreateManagerResidentPayload,
  type ManagerResidentDetail,
  type ManagerResidentRow,
  type ManagerResidentsFilter,
  type ManagerResidentsFilterData,
  type ManagerResidentsListData,
  type ManagerResidentStatusOption,
  type ManagerResidentsClusterOption,
} from './types';

type ApiSuccessResponse<T> = {
  status: 'success';
  msg: T;
};

type ApiErrorResponse = {
  status: 'error';
  msg: string;
};

type ApiClusterGroup = {
  id: number | string;
  name: string;
  residents?: ApiResident[];
};

type ApiResident = {
  id: number;
  name: string;
  email?: string;
  phone?: string;
  status?: string;
  fio?: string;
};

type ApiFilterData = {
  status?: Array<{ id: number | string; name: string; code: string }>;
  cluster?: Array<{ id: number | string; name: string }>;
};

type ApiResidentDetail = {
  id: number;
  name?: string;
  description?: string;
  image?: string | null;
  website?: string;
  inn?: string;
  ogrn?: string;
  shortName?: string;
  legalAddress?: string;
  actualAddress?: string;
  status?: string;
  phone?: string;
  email?: string;
  shortDescription?: string;
  cluster?: string;
  businessArea?: string;
  user_name?: string;
  user_last_name?: string;
  user_second_name?: string;
  user_work_position?: string;
  user_phone?: string;
  user_email?: string;
};

let cachedFilterData: ManagerResidentsFilterData | null = null;

async function parseJsonResponse<T>(response: Response): Promise<T> {
  const text = await response.text();

  try {
    return JSON.parse(text) as T;
  } catch {
    throw new Error('Сервер вернул некорректный ответ');
  }
}

async function parsePersonalApi<T>(response: Response, fallbackMessage: string): Promise<T> {
  const result = await parseJsonResponse<ApiSuccessResponse<T> | ApiErrorResponse>(response);

  if (!response.ok || result.status !== 'success') {
    throw new Error(
      result.status === 'error' && result.msg ? result.msg : fallbackMessage,
    );
  }

  return result.msg;
}

function flattenResidents(clusters: ApiClusterGroup[]): ManagerResidentRow[] {
  const rows: ManagerResidentRow[] = [];

  for (const cluster of clusters) {
    for (const resident of cluster.residents ?? []) {
      rows.push({
        id: resident.id,
        company: resident.name,
        cluster: cluster.name,
        manager: resident.fio?.trim() || '—',
        phone: resident.phone || '—',
        email: resident.email || '—',
        status: resident.status,
      });
    }
  }

  return rows.sort((a, b) => b.id - a.id);
}

function paginateItems(items: ManagerResidentRow[], page: number): ManagerResidentsListData['nav'] & {
  items: ManagerResidentRow[];
} {
  const safePage = page > 0 ? page : 1;
  const start = (safePage - 1) * MANAGER_RESIDENTS_PAGE_SIZE;
  const pageItems = items.slice(start, start + MANAGER_RESIDENTS_PAGE_SIZE);

  return {
    items: pageItems,
    totalCount: items.length,
    pageSize: MANAGER_RESIDENTS_PAGE_SIZE,
    currentPage: safePage,
    pageCount: Math.max(1, Math.ceil(items.length / MANAGER_RESIDENTS_PAGE_SIZE)),
  };
}

function mapFilterData(data: ApiFilterData): ManagerResidentsFilterData {
  return {
    statuses: (data.status ?? []).map((item) => ({
      id: String(item.id),
      name: item.name,
      code: item.code,
    })),
    clusters: (data.cluster ?? []).map((item) => ({
      id: String(item.id),
      name: item.name,
    })),
  };
}

function mapApiResidentDetail(
  data: ApiResidentDetail,
  filterData: ManagerResidentsFilterData,
): ManagerResidentDetail {
  const statusOption =
    filterData.statuses.find((item) => item.code === data.status) ??
    filterData.statuses.find((item) => item.id === data.status) ??
    filterData.statuses.find((item) => item.name === data.status);

  const clusterOption = filterData.clusters.find((item) => item.name === data.cluster);
  const contactFullName =
    [data.user_last_name, data.user_name, data.user_second_name].filter(Boolean).join(' ').trim() ||
    '—';

  return {
    id: Number(data.id),
    status: {
      id: statusOption?.id ?? String(data.status ?? ''),
      name: statusOption?.name ?? data.status ?? '—',
    },
    statusOptions: filterData.statuses.map((item) => ({
      id: item.id,
      name: item.name,
    })),
    logoUrl: data.image ?? '',
    fullName: data.name ?? '—',
    shortName: data.shortName ?? '—',
    projectDescription: data.description ?? data.shortDescription ?? '',
    inn: data.inn ?? '—',
    ogrn: data.ogrn ?? '—',
    legalAddress: data.legalAddress ?? '—',
    actualAddress: data.actualAddress ?? '—',
    email: data.email ?? '—',
    phone: data.phone ?? '—',
    website: data.website ?? '—',
    clusterId: clusterOption?.id ?? '',
    clusterName: data.cluster ? `«${data.cluster}»` : '—',
    contact: {
      fullName: contactFullName,
      position: data.user_work_position ?? '—',
      phone: data.user_phone ?? data.phone ?? '—',
      email: data.user_email ?? data.email ?? '—',
    },
  };
}

export async function fetchManagerResidentsFilter(
  force = false,
): Promise<ManagerResidentsFilterData> {
  if (isManagerResidentsMockEnabled()) {
    const data = {
      statuses: mockManagerResidentStatusOptions.map((item, index) => ({
        id: item.value,
        name: item.label,
        code: ['active', 'review', 'suspended'][index] ?? 'active',
      })),
      clusters: getMockManagerResidentsList({}).clusters,
    };
    cachedFilterData = data;
    return data;
  }

  if (cachedFilterData && !force) {
    return cachedFilterData;
  }

  const response = await fetch(getApiUrl('/api/personal/residents/filter/'), {
    method: 'GET',
    credentials: 'same-origin',
  });

  const data = await parsePersonalApi<ApiFilterData>(
    response,
    'Не удалось загрузить данные фильтра',
  );

  cachedFilterData = mapFilterData(data);
  return cachedFilterData;
}

export async function fetchManagerResidentsList(params: {
  q?: string;
  page?: number;
  filter?: ManagerResidentsFilter;
}): Promise<ManagerResidentsListData> {
  if (isManagerResidentsMockEnabled()) {
    return getMockManagerResidentsList(params);
  }

  const url = new URL(getApiUrl('/api/personal/residents/'), window.location.origin);

  const query = params.q?.trim();
  if (query && query.length >= 3) {
    url.searchParams.set('q', query);
  }

  if (params.filter?.clusterId) {
    url.searchParams.set('clusterId', params.filter.clusterId);
  }

  if (params.filter?.statusId) {
    url.searchParams.set('statusId', params.filter.statusId);
  }

  const response = await fetch(url.toString(), {
    method: 'GET',
    credentials: 'same-origin',
  });

  const data = await parsePersonalApi<ApiClusterGroup[]>(
    response,
    'Не удалось загрузить список резидентов',
  );

  const allItems = flattenResidents(Array.isArray(data) ? data : []);
  const paginated = paginateItems(allItems, params.page ?? 1);
  const filterData = await fetchManagerResidentsFilter();

  return {
    items: paginated.items,
    nav: {
      totalCount: paginated.totalCount,
      pageSize: paginated.pageSize,
      currentPage: paginated.currentPage,
      pageCount: paginated.pageCount,
    },
    clusters: filterData.clusters,
  };
}

export function getManagerResidentStatusOptions(
  filterData?: ManagerResidentsFilterData,
): { value: string; label: string }[] {
  if (isManagerResidentsMockEnabled()) {
    return mockManagerResidentStatusOptions;
  }

  const statuses = filterData?.statuses ?? cachedFilterData?.statuses ?? [];

  return statuses.map((item) => ({
    value: item.id,
    label: item.name,
  }));
}

export async function fetchManagerResidentDetail(
  id: number,
  fallback?: ManagerResidentRow,
  clusters: ManagerResidentsClusterOption[] = [],
): Promise<ManagerResidentDetail> {
  const filterData = await fetchManagerResidentsFilter();

  if (isManagerResidentsMockEnabled()) {
    return getMockManagerResidentDetail(id, clusters.length ? clusters : filterData.clusters);
  }

  const response = await fetch(getApiUrl(`/api/personal/residents/${id}/`), {
    method: 'GET',
    credentials: 'same-origin',
  });

  try {
    const data = await parsePersonalApi<ApiResidentDetail>(
      response,
      'Не удалось загрузить данные резидента',
    );

    if (data.id) {
      return mapApiResidentDetail(data, filterData);
    }
  } catch {
    if (!fallback) {
      throw new Error('Не удалось загрузить данные резидента');
    }
  }

  if (!fallback) {
    throw new Error('Не удалось загрузить данные резидента');
  }

  const clusterId =
    filterData.clusters.find((cluster) => cluster.name === fallback.cluster)?.id ??
    clusters[0]?.id ??
    '';

  return {
    id: fallback.id,
    status: {
      id: filterData.statuses[0]?.id ?? '',
      name: fallback.status ?? filterData.statuses[0]?.name ?? '—',
    },
    statusOptions: filterData.statuses.map((item) => ({
      id: item.id,
      name: item.name,
    })),
    logoUrl: '',
    fullName: fallback.company,
    shortName: fallback.company,
    projectDescription: '',
    inn: '—',
    ogrn: '—',
    legalAddress: '—',
    actualAddress: '—',
    email: fallback.email,
    phone: fallback.phone,
    website: '—',
    clusterId,
    clusterName: fallback.cluster ? `«${fallback.cluster}»` : '—',
    contact: {
      fullName: fallback.manager,
      position: '—',
      phone: fallback.phone,
      email: fallback.email,
    },
  };
}

async function patchManagerResident(
  id: number,
  payload: { status?: string; cluster?: string },
): Promise<string> {
  const sessid = getBitrixSessid();
  if (!sessid) {
    throw new Error('Не удалось получить CSRF-токен. Обновите страницу и попробуйте снова.');
  }

  const response = await fetch(getApiUrl(`/api/personal/residents/${id}/`), {
    method: 'PATCH',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      'X-Bitrix-Csrf-Token': sessid,
    },
    body: JSON.stringify({
      ...payload,
      sessid,
    }),
  });

  return parsePersonalApi<string>(response, 'Не удалось обновить данные резидента');
}

export async function updateManagerResidentStatus(
  id: number,
  statusId: string,
): Promise<ManagerResidentStatusOption> {
  if (isManagerResidentsMockEnabled()) {
    return updateMockManagerResidentStatus(id, statusId);
  }

  await patchManagerResident(id, { status: statusId });

  const filterData = await fetchManagerResidentsFilter(true);
  const status =
    filterData.statuses.find((item) => item.id === statusId) ??
    ({ id: statusId, name: '—' } satisfies ManagerResidentStatusOption);

  return status;
}

export async function updateManagerResidentCluster(
  id: number,
  clusterId: string,
  clusters: ManagerResidentsClusterOption[],
): Promise<{ clusterId: string; clusterName: string }> {
  if (isManagerResidentsMockEnabled()) {
    return updateMockManagerResidentCluster(id, clusterId, clusters);
  }

  await patchManagerResident(id, { cluster: clusterId });

  const filterData = await fetchManagerResidentsFilter(true);
  const cluster =
    filterData.clusters.find((item) => item.id === clusterId) ??
    clusters.find((item) => item.id === clusterId);

  return {
    clusterId,
    clusterName: cluster?.name ? `«${cluster.name}»` : '—',
  };
}

export async function createManagerResident(payload: CreateManagerResidentPayload): Promise<string> {
  if (isManagerResidentsMockEnabled()) {
    return 'Резидент успешно сохранён';
  }

  const sessid = getBitrixSessid();
  if (!sessid) {
    throw new Error('Не удалось получить CSRF-токен. Обновите страницу и попробуйте снова.');
  }

  const body = new FormData();
  body.append('sessid', sessid);
  body.append('name', payload.name);
  body.append('inn', payload.inn);
  body.append('email', payload.email);
  body.append('status', payload.status);
  body.append('cluster', payload.cluster);

  if (payload.description) body.append('description', payload.description);
  if (payload.ogrn) body.append('ogrn', payload.ogrn);
  if (payload.website) body.append('website', payload.website);
  if (payload.shortName) body.append('shortName', payload.shortName);
  if (payload.legalAddress) body.append('legalAddress', payload.legalAddress);
  if (payload.actualAddress) body.append('actualAddress', payload.actualAddress);
  if (payload.phone) body.append('phone', payload.phone);
  if (payload.shortDescription) body.append('shortDescription', payload.shortDescription);
  if (payload.businessArea) body.append('businessArea', payload.businessArea);
  if (payload.image) body.append('image', payload.image);

  const response = await fetch(getApiUrl('/api/personal/residents/'), {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'X-Bitrix-Csrf-Token': sessid,
    },
    body,
  });

  return parsePersonalApi<string>(response, 'Не удалось создать резидента');
}
